import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'

import type { CrmHistoryItem } from './CrmHistoryEvent'

/** Скільки подій тягнемо за раз: з великим запасом на історію одного запису. */
export const CRM_HISTORY_LIMIT = 100

type HistoryResponse =
    | { items?: CrmHistoryItem[]; 'hydra:member'?: CrmHistoryItem[]; meta?: { total?: number } }
    | CrmHistoryItem[]
    | null

/**
 * Стрічка «Історія змін» картки власника CRM; екземпляр — на змонтовану
 * вкладку (`SPEC-008` §5.5). Новіші події бек віддає першими.
 */
export class CrmHistoryTabStore {
    items: CrmHistoryItem[] = []
    /** Скільки подій усього — якщо більше, ніж показано, стрічка про це скаже */
    total = 0
    isLoading = false
    /** Запит не вдався: порожня стрічка тоді означала б неправду «змін не було» */
    failed = false

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * `restUrl` — колекція власника (`/services/clients`), `uuid` — запис.
     *
     * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і без нього
     * вкладка після збереження картки показала б стару стрічку.
     */
    async load(restUrl: string, uuid: string | undefined): Promise<void> {
        if (!uuid) return

        this.isLoading = true
        this.failed = false

        try {
            const raw = await restClient.get(
                `${restUrl}/${encodeURIComponent(uuid)}/history?limit=${CRM_HISTORY_LIMIT}`,
                undefined,
                { forceRefresh: true }
            )
            const data = raw as unknown as HistoryResponse
            const items = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])
            const total =
                !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : items.length

            runInAction(() => {
                this.items = Array.isArray(items) ? items : []
                this.total = total
            })
        } catch {
            runInAction(() => {
                this.items = []
                this.total = 0
                this.failed = true
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }
}
